import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { defaultFilter, FilterType, NumberRange, PartsOfDay, SortType, TimeRange } from "./types";
import { getLocation } from "@shared/location/persistentStore";

// Начальный фильтр берёт город из сохранённой локации (по умолчанию — Москва)
const initialFilter = (): FilterType => {
  const city = getLocation()?.city
  return city && city !== "Вся Россия" ? { ...defaultFilter(), city } : defaultFilter()
}

const filterSlice = createSlice({
  name: "filter",
  initialState: initialFilter,
  reducers: {
    updateCategoryId: (state, action: PayloadAction<number[] | number>) => {
      const { payload } = action
      if (Array.isArray(payload)) {
        state.eventTypeId = payload.length > 0 ? payload : []
      } else {
        const category = state.eventTypeId
        if (category) {
          const index = category.indexOf(payload)
          if (index !== -1) {
            // Снимаем выбор — если это единственная категория, сбрасываем в undefined
            const next = category.filter(id => id !== payload)
            state.eventTypeId = next.length > 0 ? next : []
          } else {
            // Добавляем категорию
            state.eventTypeId = [...category, payload]
          }
        } else {
          state.eventTypeId = [payload]
        }
      }
      // Клик по плитке категории никогда не включает «Все» — даже если снята последняя категория:
      // тогда лента пустая, пока пользователь не выберет категорию или не нажмёт «Все».
      // Программный сброс массивом (кнопка «Все», восстановление после смены города) считается как раньше.
      state.showAll = !(state.eventTypeId && state.eventTypeId.length > 0)
    },
    setQueryText: (state, action: PayloadAction<string | undefined>) => {
      state.text = action.payload
    },
    updateFilter: (_, action: PayloadAction<FilterType>) => {
      // Фильтр из адресной строки не содержит showAll — восстанавливаем его по выбранным категориям
      const next = action.payload
      return { ...next, showAll: !(next.eventTypeId && next.eventTypeId.length > 0) }
    },
    updateParticipants: (state, action: PayloadAction<NumberRange>) => {
      const [min, max] = action.payload
      state.numberOfParticipantsMin = min
      state.numberOfParticipantsMax = max
    },
    updateDateRange: (state, action: PayloadAction<TimeRange>) => {
      const [start, end] = action.payload
      state.startDate = start
      state.endDate = end
    },
    updateDuration: (state, action: PayloadAction<NumberRange>) => {
      const [min, max] = action.payload
      state.durationEventInHoursMin = min
      state.durationEventInHoursMax = max
    },
    updateSort: (state, action: PayloadAction<SortType>) => {
      const { payload } = action
      state.sort = payload
    },
    resetFilter: () => {
      return defaultFilter()
    },
    setShowAll: (state, action: PayloadAction<boolean>) => {
      state.showAll = action.payload
    },
    updatePartOfDays: (state, action: PayloadAction<PartsOfDay | undefined>) => {
      state.partsOfDay = action.payload
    },
    updateFreeArgs: (state, action: PayloadAction<boolean | undefined>) => {
      state.isFree = action.payload
    },
    updateAlcohol: (state, action: PayloadAction<boolean | undefined>) => {
      state.isPresenceOfAlcohol = action.payload
    },
    updateAgeArgs: (state, action: PayloadAction<boolean | undefined>) => {
      state.isEighteenYearLimit = action.payload
    },
    updateCityFilter: (state, action: PayloadAction<string | undefined>) => {
      state.city = action.payload
    },
    // Сброс до «Вся Россия» — убирает город и координаты, ставит showAll
    resetToAllRussia: (state) => {
      state.city = undefined
      state.latitude = undefined
      state.longitude = undefined
      state.distanceInMeters = undefined
      state.showAll = !(state.eventTypeId && state.eventTypeId.length > 0)
    },
  },
})
export const {
  updateAgeArgs,
  updateAlcohol,
  updateFreeArgs,
  updatePartOfDays,
  resetFilter,
  updateSort,
  updateDuration,
  updateDateRange,
  updateParticipants,
  updateFilter,
  updateCategoryId,
  setQueryText,
  updateCityFilter,
  setShowAll,
  resetToAllRussia
} = filterSlice.actions
export default filterSlice.reducer